import { useState, useEffect, createContext, useContext } from 'react';
import koTranslations from '../locales/ko.json';
import enTranslations from '../locales/en.json';

const translations = {
  ko: koTranslations,
  en: enTranslations
};

const LanguageContext = createContext();

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // URL 파라미터에서 언어 확인
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    
    // URL 파라미터가 있으면 우선, 없으면 localStorage, 모두 없으면 한국어
    if (urlLang && ['ko', 'en'].includes(urlLang)) {
      return urlLang;
    }
    
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'ko';
  });

  const [currentTranslations, setCurrentTranslations] = useState(translations[language]);

  useEffect(() => {
    setCurrentTranslations(translations[language]);
    localStorage.setItem('language', language);
    
    // HTML lang 속성 설정 (SEO)
    document.documentElement.lang = language;
    
    // 페이지 제목 업데이트 (SEO)
    document.title = translations[language].app.title;
    
    // 메타 description 업데이트 (SEO)
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', translations[language].meta.description);
    }
    
    // 메타 keywords 업데이트 (SEO)
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', translations[language].meta.keywords);
    }
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = currentTranslations;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
      
      // URL 파라미터 업데이트 (SEO를 위해)
      const url = new URL(window.location);
      url.searchParams.set('lang', newLanguage);
      window.history.replaceState({}, '', url);
    }
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      t,
      availableLanguages: Object.keys(translations)
    }}>
      {children}
    </LanguageContext.Provider>
  );
};